import { useContext } from "react";
import usePipeline from "@/features/stage/api/usePipeline";
import useBranches from "@/features/users/api/useBranches";
import { FormLeadContext } from "@/features/leads/contexts/FormLeadContext";

/**
 * Branch-based pipelines: the lead-detail drawer must render the pipeline of the
 * BRANCH the lead belongs to — a lead on a forked branch shows that fork's
 * stages/statuses, not the tenant default (otherwise the stage bar and the
 * Stage/Status/Sub-status pickers show the wrong — or empty — pipeline).
 *
 * Reads the viewed lead's branchId straight from FormLeadContext, so the ~10
 * drawer consumers don't each have to thread it. An explicit `branchIdOverride`
 * covers callers OUTSIDE the provider (e.g. ViewLead itself).
 *
 * ZERO-IMPACT: branchId is passed to usePipeline ONLY when the tenant has a
 * forked branch. No fork → branchId is never passed → the exact same
 * `["pipeline-stage"]` query with today's byte-identical cache key. Existing
 * (non-forked) tenants are completely unaffected.
 */
export default function useLeadPipeline(
  branchIdOverride?: number | null,
  callBack?: (data: any) => void,
) {
  const { hasForkedBranch } = useBranches();
  const ctx = useContext(FormLeadContext); // undefined outside the provider — no throw
  const branchId =
    branchIdOverride ?? ((ctx as any)?.lead?.branchId as number | null) ?? null;
  return usePipeline(
    callBack,
    hasForkedBranch ? (branchId ?? undefined) : undefined,
  );
}
